import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { useCases } from "@/data/use-cases";

export const Route = createFileRoute("/use-cases/")({
  head: () => ({
    meta: [
      {
        title: `Symbol kits by task — ${useCases.length} cross-axis collections`,
      },
      {
        name: "description",
        content: `Ready-made symbol kits for one job at a time — a social bio, a spreadsheet, a to-do list, a review, a code comment, a horoscope post. Each pulls the right symbols from several categories into one set. ${useCases.length} kits.`,
      },
      { property: "og:title", content: "Symbol kits by task" },
      {
        property: "og:description",
        content: `${useCases.length} task-based symbol kits — the right symbols for one job, in one place.`,
      },
      { property: "og:url", content: abs("/use-cases") },
    ],
    links: [{ rel: "canonical", href: abs("/use-cases") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Symbol use cases",
          url: "/use-cases",
          numberOfItems: useCases.length,
        }),
      },
    ],
  }),
  component: UseCasesIndex,
});

function UseCasesIndex() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <span>/</span>
          <span className="text-foreground">Use cases</span>
        </nav>

        <h1 className="font-display text-4xl font-bold sm:text-5xl">
          Symbol kits, by <span className="text-gradient">task</span>
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Ready-made kits organized around <em>what you're actually doing</em> —
          decorating a social bio, marking up a spreadsheet, building a checklist,
          scoring a review, or tagging a horoscope post. Each kit pulls the right
          symbols from several categories into one place, so you don't have to
          hunt across pages.
        </p>

        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link to="/categories" className="text-accent hover:underline">
            Or browse by category →
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {useCases.map((u) => (
            <Link
              key={u.slug}
              to="/use-cases/$slug"
              params={{ slug: u.slug }}
              className="group glass rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-glow)]"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-display text-lg font-semibold">
                  {u.name}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {u.examples.length} examples
                </span>
              </div>
              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                {u.tagline}
              </p>
              <p className="mt-3 truncate text-lg text-foreground/90" aria-hidden="true">
                {u.examples[0]?.text.split("\n")[0]}
              </p>
            </Link>
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
